import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, ErrorState, IconAction, Pagination, Select, TextSkeleton, cn,
} from '@dru/ui';
import { Bell, Check, Lock, Mail, MessageSquare, RotateCcw, Send } from 'lucide-react';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage } from '../../../lib/auth';
import { categoryOf } from '../../../lib/notificationCategories';

type Channel = 'IN_APP' | 'EMAIL' | 'SMS';
type Audience = 'EMPLOYEE' | 'COMPANY' | 'LAB' | 'PHYSICIAN' | 'CAMP' | 'PLATFORM' | 'ANYONE';

interface EventRow {
  event: string;
  audience: Audience;
  category: string;
  label: string;
  when: string;
  allowed: Channel[];
  defaults: Channel[];
  required: Channel[];
  locked?: boolean;
  enabled: Channel[];
  customised: boolean;
}

interface LogRow {
  id: string; event: string; label: string; channel: Channel; status: string; to: string;
  subject: string | null; attempts: number; sentAt: string | null; lastError: string | null;
  createdAt: string; company: string | null;
}
interface Log { items: LogRow[]; total: number; page: number; pageSize: number; failedLastWeek: number }

const CHANNELS: Array<{ key: Channel; label: string; icon: typeof Bell }> = [
  { key: 'IN_APP', label: 'In-app', icon: Bell },
  { key: 'EMAIL', label: 'Email', icon: Mail },
  { key: 'SMS', label: 'SMS', icon: MessageSquare },
];

const AUDIENCES: Array<{ key: Audience; title: string; hint: string }> = [
  { key: 'EMPLOYEE', title: 'Employees', hint: 'In the employee app, and by SMS or email.' },
  { key: 'COMPANY', title: 'Company HR', hint: 'People in a company console who hold the right permission.' },
  { key: 'LAB', title: 'Labs and hospitals', hint: 'Venue staff, on the website and in the camp app.' },
  { key: 'CAMP', title: 'Camp staff', hint: 'In the camp app.' },
  { key: 'PHYSICIAN', title: 'Physicians', hint: 'The doctor the case is assigned to.' },
  { key: 'PLATFORM', title: 'Super Admin and Operations', hint: 'Platform users who hold the permission for that work.' },
  { key: 'ANYONE', title: 'Sign-in and accounts', hint: 'Always sent. These are how people get into their account.' },
];

/**
 * Settings → Notifications: which notification goes on which channel, and
 * the record of what was sent. A switch saves at once and applies to the
 * next message; nothing already queued changes.
 */
export function NotificationSettings() {
  const [view, setView] = useState<'events' | 'log'>('events');
  const log = useQuery<Log>(() => api.get('/admin/notifications/log?status=FAILED'), []);
  const failed = log.data?.failedLastWeek ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div role="group" aria-label="Show" className="inline-flex h-10 rounded-btn border border-neutral-200 p-0.5">
          {([['events', 'What is sent'], ['log', 'Delivery log']] as const).map(([k, l]) => (
            <button
              key={k} type="button" aria-pressed={view === k} onClick={() => setView(k)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-[6px] px-3.5 text-small font-medium transition-colors',
                view === k ? 'bg-primary-50 text-primary-800' : 'text-neutral-600 hover:text-neutral-900',
              )}
            >
              {l}
              {k === 'log' && failed > 0 && (
                <span className="rounded-badge bg-danger-600 px-1.5 text-micro leading-4 text-white tabular-nums">{failed}</span>
              )}
            </button>
          ))}
        </div>
        {view === 'events' && (
          <p className="text-caption text-neutral-500">Changes apply to the next message. Wording is on the Email page.</p>
        )}
      </div>
      {view === 'events' ? <EventMatrix /> : <DeliveryLog />}
    </div>
  );
}

function EventMatrix() {
  const q = useQuery<{ items: EventRow[] }>(() => api.get('/admin/notifications/events'), []);
  const [rows, setRows] = useState<EventRow[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);
  useEffect(() => { if (q.data) setRows(q.data.items); }, [q.data]);

  const groups = useMemo(() => AUDIENCES
    .map((a) => ({ ...a, items: rows.filter((r) => r.audience === a.key) }))
    .filter((g) => g.items.length > 0), [rows]);

  const save = async (r: EventRow, channels: Channel[] | null) => {
    setBusy(`${r.event}:save`); setNote(null);
    const before = rows;
    if (channels) setRows((s) => s.map((x) => (x.event === r.event ? { ...x, enabled: channels, customised: true } : x)));
    try {
      const res = await api.put<{ enabled: Channel[]; customised: boolean }>(`/admin/notifications/events/${r.event}`, { channels });
      setRows((s) => s.map((x) => (x.event === r.event ? { ...x, ...res } : x)));
    } catch (e) {
      setRows(before);
      setNote({ tone: 'danger', text: errorMessage(e) });
    } finally { setBusy(null); }
  };
  const flip = (r: EventRow, c: Channel) =>
    save(r, r.enabled.includes(c) ? r.enabled.filter((x) => x !== c) : [...r.enabled, c]);

  const test = async (r: EventRow) => {
    setBusy(`${r.event}:test`); setNote(null);
    try {
      await api.post(`/admin/notifications/events/${r.event}/test`, {});
      setNote({ tone: 'success', text: `A test “${r.label}” was sent to you. Watch the bell.` });
    } catch (e) { setNote({ tone: 'danger', text: errorMessage(e) }); } finally { setBusy(null); }
  };

  if (q.error) return <ErrorState title="Could not load notifications" detail={q.error} onRetry={q.reload} />;
  if (q.loading && rows.length === 0) return <Card className="p-5"><TextSkeleton lines={8} /></Card>;

  return (
    <div className="space-y-4">
      {note && <Alert tone={note.tone}>{note.text}</Alert>}
      {groups.map((g) => (
        <Card key={g.key} className="overflow-hidden">
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-neutral-200 bg-neutral-50/60 px-4 py-3">
            <h3 className="text-small font-semibold text-neutral-900">{g.title}</h3>
            <p className="text-caption text-neutral-500">{g.hint}</p>
          </div>
          <ul className="divide-y divide-neutral-100">
            {g.items.map((r) => {
              const meta = categoryOf(r.category);
              return (
                <li key={r.event} className="flex flex-col gap-3 px-4 py-3 md:flex-row md:items-center">
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    <span aria-hidden className={cn('mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full', meta.tone)}>
                      <meta.icon className="h-4 w-4" />
                    </span>
                    <div className="min-w-0">
                      <p className="flex flex-wrap items-center gap-2 text-small font-medium text-neutral-900">
                        {r.label}
                        {r.customised && <span className="rounded-badge bg-primary-50 px-1.5 text-micro font-medium text-primary-700">Changed</span>}
                      </p>
                      <p className="text-caption text-neutral-500">{r.when}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 pl-11 md:pl-0">
                    {CHANNELS.map((c) => {
                      const allowed = r.allowed.includes(c.key);
                      const on = r.enabled.includes(c.key);
                      const fixed = r.locked || r.required.includes(c.key);
                      if (!allowed) {
                        return (
                          <span key={c.key} className="inline-flex h-9 w-[5.75rem] items-center justify-center rounded-btn border border-dashed border-neutral-200 text-caption text-neutral-300" title={`${c.label} is not used for this`}>
                            —
                          </span>
                        );
                      }
                      return (
                        <button
                          key={c.key} type="button" aria-pressed={on}
                          disabled={fixed || busy === `${r.event}:save`}
                          onClick={() => void flip(r, c.key)}
                          title={fixed ? `${c.label} is always sent` : on ? `Stop sending by ${c.label}` : `Also send by ${c.label}`}
                          className={cn(
                            'inline-flex h-9 w-[5.75rem] items-center justify-center gap-1.5 rounded-btn border text-caption font-medium transition-colors',
                            on ? 'border-primary-300 bg-primary-50 text-primary-800' : 'border-neutral-200 bg-white text-neutral-500 hover:bg-neutral-50',
                            fixed && 'cursor-default',
                          )}
                        >
                          {fixed ? <Lock className="h-3.5 w-3.5" aria-hidden /> : on ? <Check className="h-3.5 w-3.5" aria-hidden /> : <c.icon className="h-3.5 w-3.5" aria-hidden />}
                          {c.label}
                        </button>
                      );
                    })}
                    {/* Fixed width, so every row's switches line up. */}
                    <span className="ml-1 inline-flex w-[5.5rem] items-center">
                    {!r.locked && (
                      <>
                        <IconAction label={`Send me a test: ${r.label}`} icon={Send} loading={busy === `${r.event}:test`} onClick={() => void test(r)} />
                        {r.customised && (
                          <IconAction label={`Back to the default for ${r.label}`} icon={RotateCcw} loading={busy === `${r.event}:save`} onClick={() => void save(r, null)} />
                        )}
                      </>
                    )}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      ))}
    </div>
  );
}

const STATUS_TONE: Record<string, string> = {
  SENT: 'bg-success-100 text-success-600',
  PENDING: 'bg-info-100 text-info-600',
  SENDING: 'bg-info-100 text-info-600',
  FAILED: 'bg-danger-100 text-danger-600',
  SUPPRESSED: 'bg-neutral-100 text-neutral-600',
};
const STATUS_LABEL: Record<string, string> = {
  SENT: 'Sent', PENDING: 'Waiting', SENDING: 'Sending', FAILED: 'Failed', SUPPRESSED: 'Not sent',
};

function DeliveryLog() {
  const [status, setStatus] = useState('');
  const [channel, setChannel] = useState('');
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const log = useQuery<Log>(() => {
    const p = new URLSearchParams({ page: String(page) });
    if (status) p.set('status', status);
    if (channel) p.set('channel', channel);
    return api.get(`/admin/notifications/log?${p}`);
  }, [status, channel, page]);

  const retry = async (r: LogRow) => {
    setBusy(r.id); setError(null);
    try { await api.post(`/admin/notifications/log/${r.id}/retry`, {}); log.reload(); }
    catch (e) { setError(errorMessage(e)); } finally { setBusy(null); }
  };

  const d = log.data;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} aria-label="Status" className="h-10 w-40">
          <option value="">Every status</option>
          <option value="FAILED">Failed</option>
          <option value="PENDING">Waiting</option>
          <option value="SENT">Sent</option>
          <option value="SUPPRESSED">Not sent</option>
        </Select>
        <Select value={channel} onChange={(e) => { setChannel(e.target.value); setPage(1); }} aria-label="Channel" className="h-10 w-40">
          <option value="">Every channel</option>
          <option value="IN_APP">In-app</option>
          <option value="EMAIL">Email</option>
          <option value="SMS">SMS</option>
        </Select>
      </div>
      {error && <Alert tone="danger">{error}</Alert>}
      {log.error ? <ErrorState title="Could not load the log" detail={log.error} onRetry={log.reload} />
        : log.loading && !d ? <Card className="p-5"><TextSkeleton lines={6} /></Card>
        : !d || d.items.length === 0 ? (
          <Card className="px-4 py-10 text-center text-small text-neutral-500">Nothing here.</Card>
        ) : (
          <Card className="overflow-hidden">
            <ul className="divide-y divide-neutral-100">
              {d.items.map((r) => {
                const ch = CHANNELS.find((c) => c.key === r.channel);
                return (
                  <li key={r.id} className="grid gap-x-4 gap-y-1 px-4 py-3 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_auto] md:items-center">
                    <div className="min-w-0">
                      <p className="truncate text-small font-medium text-neutral-900">{r.label}</p>
                      <p className="truncate text-caption text-neutral-500">{r.subject ?? '—'}</p>
                    </div>
                    <div className="min-w-0 text-caption text-neutral-600">
                      <p className="flex items-center gap-1.5">
                        {ch && <ch.icon className="h-3.5 w-3.5 shrink-0 text-neutral-400" aria-hidden />}
                        <span className="truncate">{ch?.label} · {r.to}</span>
                      </p>
                      <p className="truncate text-neutral-500">
                        {r.company ?? 'Platform'} · {new Date(r.createdAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}
                      </p>
                      {r.lastError && r.status !== 'SENT' && <p className="mt-0.5 line-clamp-2 text-danger-600">{r.lastError}</p>}
                    </div>
                    <div className="flex items-center gap-2 md:justify-end">
                      <span className={cn('rounded-badge px-2 py-0.5 text-micro font-medium', STATUS_TONE[r.status] ?? STATUS_TONE.SUPPRESSED)}>
                        {STATUS_LABEL[r.status] ?? r.status}
                      </span>
                      {r.status === 'FAILED' && r.channel !== 'IN_APP' && (
                        <Button size="sm" variant="secondary" loading={busy === r.id} onClick={() => void retry(r)}>Retry</Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            <div className="border-t border-neutral-100 px-4 py-2">
              <Pagination page={d.page} pageSize={d.pageSize} total={d.total} onPageChange={setPage} />
            </div>
          </Card>
        )}
    </div>
  );
}
